import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import HvnlyNabAlert from './Alert/HvnlyNabAlert';
import PropertyBuilder from './builder';
import './BuilderApp.css';
import { usePreloader } from './hooks/useBuilderPreloader';
import LoadingOverlay from './LoadingOverlay/LoadingOverlay';
import Toast from './Toast/Toast';
import Preloader from './utils/builderPreloader';

const BuilderApp = () => {
  const [toasts, setToasts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPropertyLoading, setIsPropertyLoading] = useState(true);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(false);
  
  // Toast position - can be changed dynamically
  const [toastPosition, setToastPosition] = useState('top-center'); // Changed to top-center
  
  // Track toast IDs to prevent duplicates
  const lastToastRef = useRef({ id: null, time: 0 });

  // Use your existing usePreloader hook
  const {
    preloaderVisible,
    isExiting,
    loadingProgress,
    loadingStage,
    setLoadingProgress,
    setLoadingStage
  } = usePreloader(true);

  // Enhanced toast helpers with position support
  const showToast = useCallback((
    type, 
    title, 
    message, 
    position = toastPosition // Use current position if not specified
  ) => {
    // Create a unique key for this toast
    const toastKey = `${type}-${title}-${message}-${position}`;
    const now = Date.now();
    
    // Prevent duplicate toasts within 2 seconds
    if (lastToastRef.current.id === toastKey && now - lastToastRef.current.time < 2000) {
     // console.log('🛑 Duplicate toast prevented:', toastKey);
      return;
    }
    
    // Update last toast reference
    lastToastRef.current = {
      id: toastKey,
      time: now
    };
    
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message, position }]);
    
    // Show loading overlay only for save operations
    if (type === 'success' && (title.includes('Saved') || title.includes('Updated') || title.includes('Sidebar'))) {
      setLoadingOverlayVisible(true);
      setTimeout(() => {
        setLoadingOverlayVisible(false);
      }, 2000);
    }
  }, [toastPosition]);

  // Function to change toast position dynamically
  const changeToastPosition = useCallback((newPosition) => {
    setToastPosition(newPosition);
    // Optional: Show a toast confirming position change
    showToast('info', __('Position Updated', 'havenlytics'), `Toasts will now appear at ${newPosition}`, newPosition);
  }, [showToast]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Handle property loading state changes
  const handlePropertyLoadingChange = useCallback((loading) => {
    //console.log('🔄 Property loading state changed:', loading);
    setIsPropertyLoading(loading);
    
    if (!loading) {
      setLoadingProgress(100);
    }
  }, [setLoadingProgress]);

  // Simulate initial loading stages
  useEffect(() => {
    if (isPropertyLoading) {
      const stages = [
        { stage: __('Initializing Property Builder...', 'havenlytics'), progress: 20 },
        { stage: __('Loading form configuration...', 'havenlytics'), progress: 50 },
        { stage: __('Setting up field types...', 'havenlytics'), progress: 80 },
        { stage: __('Almost ready...', 'havenlytics'), progress: 95 }
      ];

      let currentStage = 0;

      const progressInterval = setInterval(() => {
        if (currentStage < stages.length) {
          setLoadingStage(stages[currentStage].stage);
          setLoadingProgress(stages[currentStage].progress);
          currentStage++;
        } else {
          clearInterval(progressInterval);
        }
      }, 500);

      return () => clearInterval(progressInterval);
    }
  }, [isPropertyLoading, setLoadingStage, setLoadingProgress]);

  // Get container styles based on position
  const getContainerStyles = () => {
    switch(toastPosition) {
      case 'top-right':
        return {
          top: '1.5rem',
          right: '1.5rem',
          left: 'auto',
          bottom: 'auto',
          transform: 'none',
          alignItems: 'flex-end'
        };
      case 'top-left':
        return {
          top: '1.5rem',
          left: '1.5rem',
          right: 'auto',
          bottom: 'auto',
          transform: 'none',
          alignItems: 'flex-start'
        };
      case 'top-center':
        return {
          top: '1.5rem',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translateX(-50%)',
          alignItems: 'center'
        };
      case 'bottom-right':
        return {
          bottom: '1.5rem',
          right: '1.5rem',
          top: 'auto',
          left: 'auto',
          transform: 'none',
          alignItems: 'flex-end'
        };
      case 'bottom-left':
        return {
          bottom: '1.5rem',
          left: '1.5rem',
          top: 'auto',
          right: 'auto',
          transform: 'none',
          alignItems: 'flex-start'
        };
      case 'bottom-center':
        return {
          bottom: '1.5rem',
          left: '50%',
          top: 'auto',
          right: 'auto',
          transform: 'translateX(-50%)',
          alignItems: 'center'
        };
      default:
        return {
          top: '1.5rem',
          right: '1.5rem',
          alignItems: 'flex-end'
        };
    }
  };

  return (
    <div className="hvnly--builder--app">
      {/* Preloader at the app level */}
      <Preloader 
        isLoading={preloaderVisible}
        isExiting={isExiting}
        title={__("Property Builder", "havenlytics")}
        stage={loadingStage}
        progress={loadingProgress}
        tips={__("💡 Tip: Required tabs (Basic Info & Amenities Sidebar) cannot be moved or deleted", "havenlytics")}
      />
      
      {/* Include the alert component */}
      <HvnlyNabAlert />
      
      {!preloaderVisible && (
        <div className="hvnly--builder---api__container">
          <PropertyBuilder 
            showToast={showToast} 
            setIsLoading={setIsLoading}
            onPropertyLoadingChange={handlePropertyLoadingChange}
          />

          <LoadingOverlay isLoading={loadingOverlayVisible} />

          {/* Toast Container with dynamic position - THIS IS CRITICAL */}
          <div 
            id="hvnlyToastContainer" 
            className="hvnly-toast-container"
            style={getContainerStyles()}
            data-position={toastPosition}
          >
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                type={toast.type}
                title={toast.title}
                message={toast.message}
                onClose={() => removeToast(toast.id)}
                // Note: position prop on individual Toast is just for data attribute, not styling
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderApp;